import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefusedUploads } from "@/components/RefusedUploads";
import {
  AlertTriangle, CheckCircle2, Eye, FileSpreadsheet, Info, KeyRound, Loader2, RotateCcw, Upload,
} from "lucide-react";

/** EXACT BUILD-SHEET READERS — read it, look at it, then press the button yourself.
 *
 *  His standard: every value read exactly as the sheet says, in the right box, nothing missed,
 *  nothing invented — and nothing written until he has seen what WOULD happen. So the upload is a
 *  PREVIEW: five outcomes, each with its reason and the cell it came from. Apply writes them with
 *  their provenance; Undo takes every one of them back off. */

const OUTCOME = {
  fill: { cls: "bg-emerald-100 text-emerald-800", word: "FILL" },
  refresh: { cls: "bg-sky-100 text-sky-800", word: "REFRESH" },
  beside: { cls: "bg-amber-100 text-amber-900", word: "BESIDE" },
  protected: { cls: "bg-slate-200 text-slate-700", word: "PROTECTED" },
  unchanged: { cls: "bg-slate-100 text-slate-500", word: "SAME" },
};

export function SheetReaderPanel({ donorId, canManage = false, canSeeKeyCode = false, incoming = null, onApplied, testid = "sheet-reader" }) {
  const [reads, setReads] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasKey, setHasKey] = useState(false);
  const [keyCode, setKeyCode] = useState(null);
  const [refusedTick, setRefusedTick] = useState(0);
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  const handled = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, k] = await Promise.all([
        api.get(`/donors/${donorId}/sheet-reads`),
        api.get(`/donors/${donorId}/key-code/exists`).catch(() => ({ data: {} })),
      ]);
      setReads(data.reads || []);
      setHasKey(Boolean(k.data?.has_key_code));
    } catch { setReads([]); }
  }, [donorId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setKeyCode(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/sheet-readers/preview?donor_id=${donorId}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.reader.label} — read, and nothing written yet. Look it over, then press the button.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That file could not be read.", { duration: 14000 });
      if (e.response) setRefusedTick((t) => t + 1);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // AN EXCEL PICKED ON THE TRUCK'S "BUILD SHEET" BUTTON comes here and is read — the same door as the button below.
  useEffect(() => {
    if (!incoming?.file || handled.current === incoming.at) return;
    handled.current = incoming.at;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    upload(incoming.file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sheet-readers/${preview.id}/apply`);
      toast.success("On the truck — every value with the cell it came from. Undo sits beside it.");
      setPreview(null);
      setReads((r) => [data, ...r.filter((x) => x.id !== data.id)]);
      await load();
      onApplied?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That could not be applied.");
    } finally { setBusy(false); }
  };

  const undo = async (read) => {
    if (!window.confirm(`Take every value this ${read.reader.label} gave back off this truck? It will read exactly as it did before.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sheet-readers/${read.id}/undo`);
      toast.success(data.message);
      await load();
      onApplied?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That could not be undone.");
    } finally { setBusy(false); }
  };

  const askTheDealer = async () => {
    try {
      const { data } = await api.get("/dealer-ask", { params: { donor_id: donorId }, responseType: "text" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open the ask sheet.");
    }
  };

  const reveal = async () => {
    try {
      const { data } = await api.get(`/donors/${donorId}/key-code`);
      setKeyCode(data);
      if (!data.has_key_code) toast.info(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That could not be shown.");
    }
  };

  const counts = preview?.counts || {};
  const groups = ["fill", "refresh", "beside", "protected", "unchanged"];

  return (
    <div data-testid={testid} ref={rootRef} className="space-y-3 scroll-mt-20">
      <RefusedUploads params={{ donor_id: donorId, where: "truck_dealer_sheet" }} refreshKey={refusedTick} testid={`${testid}-refused`} />
      <p className="text-[11px] text-slate-500">
        An <b>exact</b> reader reads the maker's own file character for character — no AI, nothing guessed.
        Two are built: <b>Freightliner's dealer sheet</b> (<span className="font-mono">.xlsx</span>, <span className="font-mono">.xls</span> or <span className="font-mono">.csv</span>) and
        <b> GM factory option codes</b> (<span className="font-mono">.pdf</span>, codes kept exactly as printed and keyed by make).
        A truck with no build sheet takes the file as its build sheet when you put the values on.
        Any other build sheet goes on as a <b>Build sheet</b> and is read by the AI reader, where every value still needs your ✓.
      </p>

      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv" className="hidden"
            data-testid={`${testid}-file`} onChange={(e) => upload(e.target.files?.[0])} />
          <Button size="sm" variant="outline" disabled={busy} data-testid={`${testid}-upload`}
            onClick={() => fileRef.current?.click()}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span className="ml-1.5">Read a dealer sheet or option-code sheet</span>
          </Button>
          <span className="text-[11px] text-slate-400">Reading writes nothing — you press the button.</span>
          <button type="button" data-testid={`${testid}-dealer-ask`} onClick={askTheDealer}
            className="text-[12px] font-bold text-[#B01E28] underline underline-offset-2 hover:text-[#8f1820]">
            No build record for this truck? Print the dealer ask sheet
          </button>
        </div>
      )}

      {/* ---------------------------------------------------------------- THE PREVIEW */}
      {preview && (
        <div data-testid={`${testid}-preview`} className="border border-slate-300 rounded-lg overflow-hidden">
          <div className="bg-slate-900 text-white px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileSpreadsheet className="w-4 h-4" />
              {preview.reader.label}
              <Badge className="bg-emerald-600 text-white text-[10px]">EXACT — no AI</Badge>
            </div>
            <div data-testid={`${testid}-preview-counts`} className="text-[11px] text-slate-300 mt-1">
              {preview.file?.name} · {preview.sheet?.rows} rows × {preview.sheet?.columns} columns ·
              {" "}<b className="text-emerald-300">{counts.fill || 0} fill</b> ·
              {" "}<b className="text-sky-300">{counts.refresh || 0} refresh</b> ·
              {" "}<b className="text-amber-300">{counts.beside || 0} beside</b> ·
              {" "}{counts.protected || 0} protected · {counts.unchanged || 0} already the same
              {counts.unsure ? <> · <b className="text-amber-200">{counts.unsure} marked unsure</b></> : null}
            </div>
            <div className="text-[11px] text-slate-400">Nothing has been written to this truck yet.</div>
          </div>

          {(preview.disagreements || []).length > 0 && (
            <div data-testid={`${testid}-disagreements`} className="bg-red-50 border-b border-red-200 px-3 py-2 space-y-1">
              {preview.disagreements.map((x, i) => (
                <div key={i} className="text-[12px] text-red-800 flex gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span><b>{x.what}</b> — {x.on_the_truck ? <>the truck says <b>{x.on_the_truck}</b>, the sheet says <b>{x.on_the_sheet}</b>. </> : <>already on <b>{x.already_on}</b>. </>}{x.say}</span>
                </div>
              ))}
            </div>
          )}

          <div className="max-h-[460px] overflow-y-auto divide-y divide-slate-100">
            {groups.map((g) => {
              const rows = (preview.rows || []).filter((r) => r.outcome === g);
              if (!rows.length) return null;
              return (
                <div key={g} data-testid={`${testid}-group-${g}`}>
                  <div className={`px-3 py-1 text-[10px] font-mono uppercase tracking-wider ${OUTCOME[g].cls}`}>
                    {OUTCOME[g].word} — {rows.length}
                  </div>
                  {rows.map((r, i) => (
                    <div key={i} data-testid={`${testid}-row-${r.where?.cell}`} className="px-3 py-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12.5px]">
                      <span className="font-semibold text-slate-700">{r.label}</span>
                      <span className="font-mono text-slate-900">{String(r.value)}</span>
                      {r.current != null && r.outcome !== "fill" && (
                        <span className="text-slate-400">was <span className="font-mono">{String(r.current)}</span></span>
                      )}
                      <span className="text-[10px] font-mono text-slate-400">{r.where?.sheet}!{r.where?.cell}</span>
                      {r.unsure && <span className="text-[11px] text-amber-700">unsure — {r.unsure}</span>}
                      <span className="text-[11px] text-slate-400 w-full">{r.why}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="bg-slate-50 border-t border-slate-200 px-3 py-2 text-[11px] text-slate-500 space-y-1">
            <div data-testid={`${testid}-components`}>
              <b>{(preview.components || []).length} components</b> become their own records, so they travel with the part when it is pulled:{" "}
              {(preview.components || []).map((c) => c.printed_name).join(" · ")}
            </div>
            {(preview.options || []).length > 0 && (
              <div data-testid={`${testid}-options`}>
                <b>{preview.options.length} factory option codes</b>
                {preview.make ? <> under the make the sheet names — <b>{preview.make}</b></> : null} —
                kept exactly as printed, GM's own mistakes and all. They become searchable on this truck,
                and you can mark any one as not on it.
                {preview.options.some((o) => o.printed_twice) && (
                  <span className="text-amber-800"> One code is printed twice on the sheet; both readings are kept.</span>
                )}
              </div>
            )}
            {preview.key_code_found && (
              <div data-testid={`${testid}-keycode-note`} className="text-amber-800">
                <KeyRound className="w-3.5 h-3.5 inline mr-1" />
                A <b>key code</b> is on this sheet (cell {preview.key_code_where}). It is stored locked down —
                shown only to an account with the key-code permission, behind a click, and every look is logged.
              </div>
            )}
            {(preview.skipped || []).length > 0 && (
              <details data-testid={`${testid}-skipped`}>
                <summary className="cursor-pointer">{preview.skipped.length} skipped, each with its reason</summary>
                <ul className="mt-1 ml-3 list-disc">
                  {preview.skipped.map((s, i) => <li key={i}><b>{s.label}</b> — {s.why}</li>)}
                </ul>
              </details>
            )}
            {(preview.unknown || []).length > 0 && (
              <div data-testid={`${testid}-unknown`} className="text-amber-800">
                <Info className="w-3.5 h-3.5 inline mr-1" />
                {preview.unknown.length} column(s) this reader has never seen before — read and kept verbatim, and named here:{" "}
                {preview.unknown.map((u) => u.label).join(", ")}
              </div>
            )}
          </div>

          {canManage && (
            <div className="bg-white border-t border-slate-200 px-3 py-2 flex flex-wrap gap-2">
              <Button size="sm" disabled={busy} data-testid={`${testid}-apply`} onClick={apply}
                className="bg-[#B01E28] hover:bg-[#8f1820] text-white">
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Put these on this truck
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} data-testid={`${testid}-discard`}
                onClick={() => setPreview(null)}>Not now</Button>
            </div>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- WHICH READER READ WHAT */}
      {reads.length > 0 && (
        <div data-testid={`${testid}-list`} className="border border-slate-200 rounded-lg divide-y divide-slate-100">
          {reads.map((r) => (
            <div key={r.id} data-testid={`${testid}-read-${r.id}`} className="px-3 py-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
              <FileSpreadsheet className="w-3.5 h-3.5 text-slate-500" />
              <span className="font-semibold text-slate-700">{r.file?.name}</span>
              <Badge variant="outline" className="text-[10px]">{r.reader?.label}</Badge>
              {r.reader?.exact
                ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">EXACT</Badge>
                : r.reader?.id
                  ? <Badge className="bg-sky-100 text-sky-800 text-[10px]">NEEDS YOUR ✓</Badge>
                  : <Badge className="bg-slate-200 text-slate-600 text-[10px]">NOT READ</Badge>}
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${r.status === "applied" ? "bg-emerald-100 text-emerald-800" : r.status === "undone" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-900"}`}>{r.status}</span>
              <span className="text-slate-400">{(r.applied_at || r.read_at || "").slice(0, 16).replace("T", " ")} · {r.applied_by || r.read_by}</span>
              {r.why && <span className="basis-full text-[11px] text-slate-500">{r.why}</span>}
              {canManage && r.status === "applied" && (
                <Button size="sm" variant="ghost" className="h-7 text-[11px] ml-auto" disabled={busy}
                  data-testid={`${testid}-undo-${r.id}`} onClick={() => undo(r)}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Undo this sheet
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------------------------------------------------------- THE KEY CODE */}
      {hasKey && canSeeKeyCode && (
        <div data-testid={`${testid}-keycode`} className="border border-amber-300 bg-amber-50 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2 text-[12px] text-amber-900">
            <KeyRound className="w-4 h-4" />
            <b>Ignition key code on file.</b>
            {!keyCode && (
              <Button size="sm" variant="outline" className="h-7 text-[11px] ml-auto"
                data-testid={`${testid}-keycode-show`} onClick={reveal}>
                <Eye className="w-3.5 h-3.5 mr-1" />Show key code
              </Button>
            )}
          </div>
          {keyCode?.has_key_code && (
            <div className="mt-1.5">
              <div data-testid={`${testid}-keycode-value`} className="font-mono text-lg text-slate-900">{keyCode.value}</div>
              <div className="text-[11px] text-amber-800">
                Read from {keyCode.cell} by the {keyCode.reader?.replace(/_/g, " ")} reader · stored {(keyCode.stored_at || "").slice(0, 10)} by {keyCode.stored_by}.
                <br />{keyCode.note}
              </div>
            </div>
          )}
        </div>
      )}
      {hasKey && !canSeeKeyCode && (
        <div data-testid={`${testid}-keycode-locked`} className="text-[11px] text-slate-500 flex items-center gap-1.5">
          <KeyRound className="w-3.5 h-3.5" />
          An ignition key code is on file for this truck. It needs the key-code permission to be shown, and every look is logged.
        </div>
      )}
    </div>
  );
}
